<template>
  <div class="easy-data-table-dark">
    <easy-data-table
      ref="dataTable"
      v-model:server-options="serverOptions"
      v-model:items-selected="checkedItems"
      table-class-name="vue3-easy-data-table table-shadow"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :headers="headers"
      :items="items"
      :prevent-context-menu-row="false"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #no-data>
        <div style="text-align: center">No data available</div>
      </template>
      <template #item="{ column, item }: { column: string; item: Item }">
        <div
          v-b-tooltip="{
            title: getItemTooltip(column, item),
            customClass: item.validationFlags ? getTooltipClass(item.validationFlags[column]) : '',
            variant: 'info',
          }"
          class="h-48"
        >
          <div
            class="wrap-table-input no-wrap"
            :class="[getCellClasses(getValidationFlag(item, column)), getTrackingNumberClass(item, column)]"
          >
            <EudrReferences
              v-if="column === 'eudr'"
              :item="item"
              :editable="assignmentStatus === REPORT_RELEASED && !assignmentSummaryStore.assignment?.archived"
              @saved="loadFromServer"
            />
            <div v-else-if="column === 'carrierHandle'" class="row carrier-cell">
              <CarrierRowWithIcon :carrier="item.carrierHandle || ''" :only-icon="false" />
            </div>
            <span v-else-if="column === 'shipmentNumber' && item.shipmentNumber" class="success shipment-cell">
              <div class="d-flex table-input align-items-center pl-3 h-100">
                <span class="tooltip-text">
                  <a :href="getLinkToTrackingPage(item.shipmentNumber, companyBranding)" target="_blank">
                    {{ capitalize(item.shipmentNumber) }}
                  </a>
                </span>
              </div>
            </span>
            <span v-else-if="column === 'shipmentNumber'" class="fatal shipment-cell">
              <div class="d-flex align-items-center pl-3 h-100">
                <span class="tooltip-text"> - </span>
              </div>
            </span>
            <span v-else-if="column === 'trackingStatus' && !item.shipmentNumber">
              <div class="d-flex align-items-center pl-3 h-100">
                <span class="tooltip-text"> - </span>
              </div>
            </span>
            <span
              v-else-if="column === 'trackingStatus'"
              :class="item.validationFlags ? getTooltipClass(item.validationFlags[column]) : ''"
            >
              <div class="table-input status-center">
                {{ getTrackingStatus(item.lastTrackingEventStatus) }}
              </div>
            </span>
            <span v-else :class="item.validationFlags ? getTooltipClass(item.validationFlags[column]) : ''">
              <select
                v-if="column === 'currency'"
                class="table-cell-select"
                @change="updateField($event, item.id, 'currency')"
              >
                <option
                  v-for="(option, key) in currencies"
                  :key="key"
                  :value="option.value"
                  :selected="item.currency == option.value"
                >
                  {{ option.value }}
                </option>
              </select>
              <select
                v-else-if="column === 'invoiceType'"
                class="table-cell-select"
                @change="updateField($event, item.id, 'invoiceType')"
              >
                <option
                  v-for="(option, key) in invoiceOptions"
                  :key="key"
                  :value="option.value"
                  :selected="item.invoiceType == option.value"
                >
                  {{ option.text }}
                </option>
              </select>
              <input
                v-else-if="numberTypeColumns.includes(column)"
                class="table-input"
                :value="item[column as keyof Item]"
                :type="numberTypeColumns.includes(column) ? 'number' : 'text'"
                :class="{ 'input-number': numberTypeColumns.includes(column) }"
                :readonly="!isItemsEditable"
                @change="updateField($event, item.id, column)"
              />
              <input
                v-else-if="column === 'commodityCode' && item.product"
                class="table-input"
                type="text"
                :value="item.product.commodityCode"
                :readonly="!isItemsEditable"
                @change="updateField($event, item.id, 'commodityCode')"
              />
              <input
                v-else-if="itemAddressColumns.includes(column)"
                class="table-input"
                type="text"
                :value="item.recipientAddress[column as keyof ItemAddress]"
                :readonly="!isItemsEditable"
                @change="updateField($event, item.id, column)"
              />
              <input
                v-else
                class="table-input"
                type="text"
                :value="item[column as keyof Item]"
                :readonly="!isItemsEditable"
                @change="updateField($event, item.id, column)"
              />
              <div class="cell-sizer">
                {{ item.recipientAddress[column as keyof ItemAddress] || item[column as keyof Item] }}
              </div>
            </span>
          </div>
        </div>
      </template>
    </easy-data-table>

    <TablePagination
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="serverItemsLength"
    />
  </div>

  <div v-if="serverItemsLength > 7" class="table-side-scroll">
    <button @click="scrollLeft">
      <mdicon name="ChevronLeft" />
    </button>
    <button @click="scrollRight">
      <mdicon name="ChevronRight" />
    </button>
  </div>
</template>

<script lang="ts">
import type { SelectOptions } from '@/types/Components/SelectOptions'
import type { Item, ItemAddress, ItemValidationFlag } from '@/types/Models/Item'
import type { ItemFilterType } from '@/types/Models/ItemFilterType'
import type { SearchFilter } from '@/types/Models/SearchFilterType'
import type { TrackingClasses } from '@/types/Models/Tracking'
import { BSpinner } from 'bootstrap-vue-next'
import { storeToRefs } from 'pinia'
import { computed, defineComponent, onMounted, type ComputedRef, type Ref, ref, watch } from 'vue'
import { INTERNATIONAL } from '@/const/AssignmentDestinationTypes'
import {
  LABELS_CREATING,
  OPTIMISING,
  PENDING,
  OPEN,
  REPORT_ACCEPTED,
  REPORT_DENIED,
  REPORT_RELEASED,
  VALIDATING,
  VALIDATION_COMPLETE,
} from '@/const/AssignmentStatuses'
import { GIFT, MIXED, OTHER, RETURN, DOCUMENTS, SALES_OF_GOODS, COMMERCIAL_SAMPLE } from '@/const/ProformaInvoiceTypes'
import { DELIVERED, DELIVERY_ISSUE, FAILED_ATTEMPT } from '@/const/TrackingEventStatuses'
import { capitalize, formatShippingError } from '@/services/Helpers'
import { scrollLeft, scrollRight } from '@/services/TableScroll'
import { getLinkToTrackingPage, getTrackingStatus } from '@/services/Tracking'
import { useAssignmentStore } from '@/store/assignment'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { useItemStore } from '@/store/item'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import currencies from '@/assets/files/currencies.json'
import countriesEU from '@/assets/files/countriesEU.json'
import EudrReferences from '@/views/Components/Assignment/EudrReferences.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import CarrierRowWithIcon from '@/views/Components/Assignment/CarrierRowWithIcon.vue'

export default defineComponent({
  name: 'ItemsTable',
  components: {
    EudrReferences,
    BSpinner,
    CarrierRowWithIcon,
    TablePagination,
    EasyDataTable,
  },
  props: {
    assignmentId: {
      type: String,
      required: true,
    },
    destinationType: {
      type: String,
      required: false,
      default: '',
    },
    filter: {
      type: Object as () => SearchFilter,
      required: false,
      default: () => ({}),
    },
  },
  setup: function (props) {
    const itemStore = useItemStore()
    const assignmentStore = useAssignmentStore()
    const assignmentSummaryStore = useAssignmentSummaryStore()

    const { items, isLoading, getItemsCount, isErrorsOnly, isLabelsErrorsOnly } = storeToRefs(itemStore)
    const { isItemsEditable, assignmentStatus } = storeToRefs(assignmentSummaryStore)

    const companyBranding = computed(() => assignmentSummaryStore.assignment?.company?.branding || null)

    const dataTable: Ref = ref()
    const checkedItems: Ref<Item[]> = ref([])
    const serverOptions: Ref<ServerOptions> = ref({ page: 1, rowsPerPage: 100 })

    const itemsVersion: ComputedRef<string> = computed(() => assignmentStore.itemsVersion)
    const hasNonEuItems: ComputedRef<boolean> = computed(() =>
      items.value.some(
        (item) =>
          item.recipientAddress?.countryCode && !countriesEU.includes(item.recipientAddress.countryCode),
      ),
    )
    const headers: ComputedRef<Header[]> = computed(() => {
      const baseHeaders: Header[] = [
        { text: 'First Name', value: 'firstName' },
        { text: 'Last Name', value: 'lastName' },
        { text: 'Company', value: 'company' },
        { text: 'Address 1', value: 'addressLine' },
        { text: 'Address 2', value: 'addressLine2' },
        { text: 'Postcode', value: 'postalCode' },
        { text: 'Country', value: 'countryCode' },
        { text: 'City', value: 'city' },
        { text: 'E-mail', value: 'email' },
        { text: 'Phone', value: 'phoneNumber' },
        { text: 'Carrier', value: 'carrierHandle' },
        { text: 'Shipment Number', value: 'shipmentNumber' },
        { text: 'Tracking Status', value: 'trackingStatus' },
        { text: 'Weight (g)', value: 'weight' },
        { text: 'Height (cm)', value: 'height' },
        { text: 'Width (cm)', value: 'width' },
        { text: 'Length (cm)', value: 'length' },
        { text: 'Estimated Cost (kr)', value: 'estimatedCost' },
        { text: 'Customer Reference', value: 'customerReference' },
        { text: 'Item Reference', value: 'orderReference' },
      ]

      if (props.destinationType === INTERNATIONAL) {
        // Insert international-specific columns before 'Customer Reference'
        const insertIndex = baseHeaders.findIndex((h) => h.value === 'customerReference')
        const internationalColumns: Header[] = [
          ...(hasNonEuItems.value ? [{ text: 'Commodity Code', value: 'commodityCode' }] : []),
          { text: 'Quantity', value: 'quantity' },
          { text: 'Invoice Type', value: 'invoiceType' },
          { text: 'Description', value: 'description' },
          { text: 'Currency', value: 'currency' },
        ]
        baseHeaders.splice(insertIndex, 0, ...internationalColumns)
      }

      if (items.value.some((item) => item.eudrAvailable && item.cn22?.products?.length)) {
        const carrierIndex = baseHeaders.findIndex((header) => header.value === 'carrierHandle')
        baseHeaders.splice(carrierIndex + 1, 0, { text: 'EUDR', value: 'eudr' })
      }
      return baseHeaders
    })

    const numberTypeColumns = ['weight', 'height', 'width', 'length', 'estimatedCost', 'quantity']
    const itemProductColumns = ['quantity', 'commodityCode', 'type']
    const itemAddressColumns = [
      'firstName',
      'lastName',
      'company',
      'addressLine',
      'addressLine2',
      'postalCode',
      'countryCode',
      'city',
      'email',
      'phoneNumber',
    ]
    const invoiceOptions: ComputedRef<SelectOptions[]> = computed(() => {
      return [SALES_OF_GOODS, GIFT, DOCUMENTS, COMMERCIAL_SAMPLE, RETURN, MIXED, OTHER].reduce(
        (acc: SelectOptions[], val) => {
          acc.push({
            value: val,
            text: (val[0]?.toString().toUpperCase() + val.slice(1).toLowerCase()).replace(/_/g, ' '),
          })
          return acc
        },
        [],
      )
    })

    const getCellClasses = (flag: ItemValidationFlag) => (!flag ? {} : { validation: true, [flag.level]: true })
    const getTooltipClass = (flag: ItemValidationFlag) => (!flag ? '' : [flag.level].join(' '))
    const getValidationFlag = (row: Item, column: string) =>
      (row.validationFlags && row.validationFlags[column]) ||
      ({ field: '', reason: '', level: '', id: '' } as ItemValidationFlag)

    const loadFromServer = async () => {
      const params: ItemFilterType = {
        page: serverOptions.value.page,
        limit: serverOptions.value.rowsPerPage,
        byColumn: 0,
        ascending: 1,
        destinationType: props.destinationType,
        onlyWithErrors: isErrorsOnly.value ? true : null,
        onlyWithLabelsErrors: isLabelsErrorsOnly.value ? true : null,
        assignmentId: props.assignmentId,
        ...props.filter,
      }

      await itemStore.fetchItems(params)
    }

    const updateField = (event: any, itemId: string, field: string) => {
      const item = items.value.find((item) => item.id === itemId)

      const { value } = event.target
      switch (true) {
        case itemAddressColumns.includes(field):
          if (item?.recipientAddress?.id) {
            itemStore.updateItemAddress(itemId, { id: item.recipientAddress.id, itemAddressField: { [field]: value } })
          }
          break
        case itemProductColumns.includes(field):
          if (item?.product?.id) {
            itemStore.updateItemProduct(itemId, { id: item.product.id, itemProductField: { [field]: value } })
          }
          break
        default:
          itemStore.updateItem({ item: { id: itemId, [field]: value } })
          break
      }
    }

    const getTrackingNumberClass = (row: Item, rowName: string) => {
      if (rowName !== 'shipmentNumber') return ''
      if (
        [
          OPEN,
          PENDING,
          VALIDATING,
          VALIDATION_COMPLETE,
          OPTIMISING,
          REPORT_RELEASED,
          REPORT_ACCEPTED,
          REPORT_DENIED,
          LABELS_CREATING,
        ].includes(assignmentStatus.value)
      ) {
        return ''
      }

      const status = getTrackingStatus(row.lastTrackingEventStatus).replace(/ /g, '_').toLowerCase()
      const { shipmentNumber, lastTrackingEventStatus } = row
      const classes: TrackingClasses = { validation: true }

      if (!shipmentNumber || [DELIVERY_ISSUE, FAILED_ATTEMPT].includes(status)) {
        classes.fatal = true
      } else if (status && status !== DELIVERED) {
        classes[status] = true
      } else if (lastTrackingEventStatus || status === DELIVERED) {
        classes.success = true
      }

      return classes
    }

    const getItemTooltip = (column: string, item: Item) => {
      if (column === 'shipmentNumber' && !item.shipmentNumber) {
        return formatShippingError(item.shippingError)
      }
      if (['trackingStatus', 'shipmentNumber'].includes(column)) {
        return getTrackingStatus(item.lastTrackingEventStatus)
      }
      if (!item.validationFlags || !item.validationFlags[column]) {
        return ''
      }

      return item.validationFlags[column] ? item.validationFlags[column].reason : ''
    }

    const updateChecked = () => {
      itemStore.updateCheckedItems(checkedItems.value.map((item: Item) => item.id))
    }

    const resetPage = () => {
      if (serverOptions.value.page !== 1) {
        serverOptions.value.page = 1
      }
      loadFromServer()
    }

    const reloadItems = () => {
      if (checkedItems.value.length > 0) {
        checkedItems.value = []
      }
      loadFromServer()
    }

    onMounted(loadFromServer)

    watch(checkedItems, updateChecked, { deep: true })
    watch(itemsVersion, reloadItems)
    watch([() => props.filter, serverOptions], loadFromServer)
    watch([() => props.destinationType, isErrorsOnly, isLabelsErrorsOnly], resetPage)

    return {
      assignmentSummaryStore,
      REPORT_RELEASED,
      loadFromServer,
      itemAddressColumns,
      numberTypeColumns,

      items,
      loading: isLoading,
      headers,
      dataTable,
      currencies,
      serverOptions,
      serverItemsLength: getItemsCount,
      isItemsEditable,
      invoiceOptions,
      checkedItems,
      companyBranding,
      getValidationFlag,
      getLinkToTrackingPage,
      getTrackingNumberClass,
      getTrackingStatus,
      getTooltipClass,
      getCellClasses,
      getItemTooltip,
      updateField,
      scrollRight,
      scrollLeft,
      capitalize,
    }
  },
})
</script>

<style lang="scss" scoped>
.table-input {
  background: transparent !important;
  width: 100%;
}
:deep(.vue3-easy-data-table__main) {
  overflow-y: hidden;
  width: 100%;
}
:deep(.vue3-easy-data-table__main thead tr th),
.h-48 {
  height: 48px;
  .easy-checkbox {
    margin: 0 !important;
  }
}
:deep(.vue3-easy-data-table__main tbody tr td) {
  height: 48px;
  min-width: 93px;
  white-space: nowrap;
  border-right: var(--easy-table-cell-border);

  .input-number {
    /* Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    &[type='number'] {
      appearance: textfield;
    }
  }

  .table-cell-select {
    background: transparent;
    border: none;
    height: 100%;
  }
  .cell-sizer {
    width: auto;
    opacity: 0;
    height: 0;
    margin-left: 34px;
  }
  .status-center {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  a {
    color: var(--easy-table-body-row-font-color);
  }
  .easy-checkbox {
    margin: 0 !important;
  }
}

:deep(.vue3-easy-data-table__main tbody tr td:first-child),
:deep(.vue3-easy-data-table__main thead tr th:first-child) {
  border-right: none;
  min-width: auto;
  padding-right: 0;
}
.easy-data-table-footer {
  margin: 20px 0 70px;
}
.carrier-cell {
  padding: 0 20px;
  margin: 0;
  height: 100%;
  min-width: 150px;
}
</style>
